'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search,
  ShoppingCart,
  Eye,
  Globe,
  Smartphone,
  Monitor,
  Code,
  Zap,
  Shield,
  Download,
  Star,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Package,
} from 'lucide-react';

const categoryIcons: Record<string, React.ElementType> = {
  '网站': Globe,
  '软件': Monitor,
  '小程序': Smartphone,
};

function CategoryIconDisplay({ category }: { category: string }) {
  if (category === '全部') {
    return <Package className="w-4 h-4 inline mr-1" />;
  }
  const IconComponent = categoryIcons[category];
  if (IconComponent) {
    return <IconComponent className="w-4 h-4 inline mr-1" />;
  }
  return null;
}

const products = [
  {
    id: 1,
    name: '企业官网模板',
    category: '网站',
    type: 'website',
    description: '现代化企业官网，包含首页、关于、服务、案例、联系等完整页面',
    price: 299,
    originalPrice: 599,
    features: ['响应式设计', 'SEO优化', '多页面支持', '部署教程', '完整源码'],
    technologies: ['Next.js', 'React', 'TailwindCSS'],
    downloads: 128,
    rating: 4.8,
    reviews: 24,
    imageUrl: 'https://coze-coding-project.tos.coze.site/coze_storage_7649362515272499236/image/generate_image_8d9342c4-1da5-49c0-9e8e-08a061905a17.jpeg?sign=1812545604-97cef8fe47-0-7fd98e212eb6e6cd3e6a865ab8fcd2f5c551ec67753f75722428bb50796a7337',
    demoUrl: 'https://demo-website.coze.site',
    sourceSize: '15MB',
  },
  {
    id: 2,
    name: '在线教育平台',
    category: '软件',
    type: 'software',
    description: '完整的在线教育系统，支持课程管理、学习追踪、在线考试、支付集成',
    price: 999,
    originalPrice: 1999,
    features: ['课程系统', '学习追踪', '在线考试', '支付集成', '管理后台'],
    technologies: ['React', 'Node.js', 'PostgreSQL', 'Stripe'],
    downloads: 56,
    rating: 4.9,
    reviews: 18,
    imageUrl: 'https://coze-coding-project.tos.coze.site/coze_storage_7649362515272499236/image/generate_image_e283e65e-3c34-4f2a-af34-010e23afb254.jpeg?sign=1812545605-17c2249318-0-de1bccda7d89c9b5ba32195374b4d73151cd1f8e3d572c2a40813585a9f1ea4b',
    demoUrl: 'https://demo-edu.coze.site',
    sourceSize: '45MB',
  },
  {
    id: 3,
    name: '智能客服小程序',
    category: '小程序',
    type: 'miniprogram',
    description: '微信小程序智能客服，集成AI对话，支持常见问题自动回复',
    price: 499,
    originalPrice: 999,
    features: ['AI对话', '知识库管理', '消息推送', '数据分析', '完整源码'],
    technologies: ['微信小程序', 'GPT API', 'Node.js'],
    downloads: 89,
    rating: 4.7,
    reviews: 32,
    imageUrl: 'https://coze-coding-project.tos.coze.site/coze_storage_7649362515272499236/image/generate_image_202fbb58-ec35-49a2-929c-91fa1b85d3d3.jpeg?sign=1812545604-5874cab922-0-bab4c865607b43cd0cf963d61935acdd6cb8228ddd6ed9c586607c63194aef70',
    demoUrl: null,
    sourceSize: '25MB',
  },
  {
    id: 4,
    name: '电商小程序模板',
    category: '小程序',
    type: 'miniprogram',
    description: '完整的电商小程序，包含商品展示、购物车、订单管理、支付流程',
    price: 699,
    originalPrice: 1399,
    features: ['商品管理', '购物车', '订单系统', '微信支付', '完整源码'],
    technologies: ['微信小程序', 'Node.js', 'MongoDB'],
    downloads: 142,
    rating: 4.6,
    reviews: 45,
    imageUrl: 'https://coze-coding-project.tos.coze.site/coze_storage_7649362515272499236/image/generate_image_2f0a8ac9-48f1-4e6b-afb5-cc139da1486d.jpeg?sign=1812545603-d80b59d931-0-78d0e1cf66d79d1357bf39e88794170e6513e760ca5ec4aa4359e50cdcb830ad',
    demoUrl: null,
    sourceSize: '30MB',
  },
  {
    id: 5,
    name: '数据仪表盘',
    category: '软件',
    type: 'software',
    description: '可视化数据分析仪表盘，支持多维度数据展示、实时更新、自定义报表',
    price: 599,
    originalPrice: 1199,
    features: ['数据可视化', '实时更新', '自定义报表', '多数据源', '完整源码'],
    technologies: ['Vue', 'ECharts', 'Node.js', 'PostgreSQL'],
    downloads: 67,
    rating: 4.8,
    reviews: 21,
    imageUrl: 'https://coze-coding-project.tos.coze.site/coze_storage_7649362515272499236/image/generate_image_160a52ac-5434-465d-a85d-61786dc97ed0.jpeg?sign=1812545605-29ad0e7ce5-0-28fb0e544b460fb86075aaa95137a4453b18c91d96bc4f6d6b4803646f6b93ff',
    demoUrl: 'https://demo-dashboard.coze.site',
    sourceSize: '35MB',
  },
  {
    id: 6,
    name: '博客系统',
    category: '网站',
    type: 'website',
    description: '现代化博客系统，支持文章管理、分类标签、评论系统、RSS订阅',
    price: 199,
    originalPrice: 399,
    features: ['文章管理', 'Markdown支持', '评论系统', 'SEO优化', '完整源码'],
    technologies: ['Next.js', 'React', 'PostgreSQL'],
    downloads: 234,
    rating: 4.5,
    reviews: 56,
    imageUrl: 'https://coze-coding-project.tos.coze.site/coze_storage_7649362515272499236/image/generate_image_95d30d5f-db1b-42ed-9d3c-296bd3e43751.jpeg?sign=1812545613-2b35e9af3e-0-f505f9f6ff611e938eb730d877a0ac14bcf6f57edb71dcaa1f758414fd68595f',
    demoUrl: 'https://demo-blog.coze.site',
    sourceSize: '12MB',
  },
];

const categories = ['全部', '网站', '软件', '小程序'];

const benefits = [
  {
    icon: Zap,
    title: '即刻可用',
    description: '付款后立即获取完整源码，无需等待开发周期',
  },
  {
    icon: Shield,
    title: '质量保证',
    description: '所有产品经过严格测试，代码规范完整',
  },
  {
    icon: Download,
    title: '包含文档',
    description: '部署教程、设计文档、技术支持一站式提供',
  },
  {
    icon: Package,
    title: '超高性价比',
    description: '现成产品价格远低于定制开发，立省50%-70%',
  },
];

export default function HomePage() {
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [searchQuery, setSearchQuery] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const filteredProducts = products.filter((product) => {
    const matchesCategory = selectedCategory === '全部' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getDiscount = (original: number, current: number) => {
    return Math.round(((original - current) / original) * 100);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section - 产品导向 */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e27] via-[#151b2e] to-[#0a0e27]" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00d4ff] rounded-full filter blur-[128px] opacity-20" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#7c3aed] rounded-full filter blur-[128px] opacity-20" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00d4ff]/50 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#151b2e] border border-[#00d4ff]/30 mb-8">
            <Sparkles className="w-4 h-4 text-[#00d4ff]" />
            <span className="text-sm text-[#a0aec0]">现成源码 · 即刻交付 · 立省50%</span>
          </div>

          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] text-transparent bg-clip-text">
              舒喆AI源码
            </span>
            <br />
            <span className="text-white">现成的网站、软件、小程序源码</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-[#a0aec0] mb-8 max-w-3xl mx-auto">
            高质量、可商用的完整源码产品，付款后即刻获取，无需漫长等待开发周期
          </p>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mb-8 sm:mb-12">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#10b981]" />
              <span className="text-white font-semibold">6+</span>
              <span className="text-[#a0aec0] text-sm sm:text-base">现成产品</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#00d4ff]" />
              <span className="text-white font-semibold">716+</span>
              <span className="text-[#a0aec0] text-sm sm:text-base">已售出</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#7c3aed]" />
              <span className="text-white font-semibold">4.7</span>
              <span className="text-[#a0aec0] text-sm sm:text-base">平均评分</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4">
            <Link href="#products" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] hover:opacity-90 text-white px-6 sm:px-8 h-11 sm:h-12">
                浏览产品库
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link href="/services" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-[#a0aec0]/30 text-[#a0aec0] hover:bg-[#151b2e] hover:text-white px-6 sm:px-8 h-11 sm:h-12">
                定制开发服务
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0a0e27]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group p-6 rounded-xl bg-[#151b2e]/50 border border-[rgba(255,255,255,0.1)] hover:border-[#00d4ff]/30 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#00d4ff]/20 to-[#7c3aed]/20">
                    <benefit.icon className="w-5 h-5 text-[#00d4ff]" />
                  </div>
                  <h3 className="text-white font-semibold">{benefit.title}</h3>
                </div>
                <p className="text-[#a0aec0] text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0a0e27] to-[#151b2e]">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              精选源码产品
            </h2>
            <p className="text-[#a0aec0] max-w-2xl mx-auto">
              每个产品都包含完整源码、部署教程、设计文档和技术支持
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 sm:gap-4 mb-8">
            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center sm:justify-start gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] text-white'
                      : 'bg-[#151b2e] text-[#a0aec0] hover:text-white hover:bg-[#151b2e]/80'
                  }`}
                >
                  <CategoryIconDisplay category={category} />
                  {category}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a0aec0]" />
              <Input
                placeholder="搜索产品..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#151b2e] border-[rgba(255,255,255,0.1)] text-white placeholder:text-[#a0aec0] focus:border-[#00d4ff]/50"
              />
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => {
              const CategoryIcon = categoryIcons[product.category] || Code;
              const discount = getDiscount(product.originalPrice, product.price);

              return (
                <Card
                  key={product.id}
                  className="group relative bg-[#151b2e]/50 border-[rgba(255,255,255,0.1)] hover:border-[#00d4ff]/30 overflow-hidden transition-all duration-300"
                >
                  {/* Discount Badge */}
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-[#10b981] text-white font-semibold">
                      -{discount}%
                    </Badge>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-4 left-4 z-10">
                    <Badge variant="outline" className="border-[#00d4ff]/30 text-[#00d4ff]">
                      <CategoryIcon className="w-3 h-3 mr-1" />
                      {product.category}
                    </Badge>
                  </div>

                  <CardHeader className="pb-3">
                    {/* Product Image */}
                    <div className="relative h-40 bg-gradient-to-br from-[#0a0e27] to-[#151b2e] rounded-lg mb-4 overflow-hidden group-hover:scale-105 transition-transform duration-300">
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#00d4ff]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <CardTitle className="text-xl text-white group-hover:text-[#00d4ff] transition-colors">
                      {product.name}
                    </CardTitle>
                    <CardDescription className="text-[#a0aec0] line-clamp-2">
                      {product.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Price */}
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-[#00d4ff]">¥{product.price}</span>
                      <span className="text-sm text-[#a0aec0] line-through">¥{product.originalPrice}</span>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm text-[#a0aec0]">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-[#10b981] fill-[#10b981]" />
                        <span>{product.rating}</span>
                        <span>({product.reviews})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        <span>{product.downloads} 已售</span>
                      </div>
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-1">
                      {product.technologies.slice(0, 3).map((tech) => (
                        <Badge key={tech} variant="outline" className="text-xs border-[rgba(255,255,255,0.05)] text-[#a0aec0]">
                          {tech}
                        </Badge>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                      <Link href={`/products/${product.id}`} className="w-full sm:flex-1">
                        <Button variant="outline" className="w-full border-[rgba(255,255,255,0.1)] text-[#a0aec0] hover:text-white hover:bg-[#151b2e]">
                          <Eye className="w-4 h-4 mr-1" />
                          查看详情
                        </Button>
                      </Link>
                      <Link href={`/products/${product.id}/purchase`} className="w-full sm:flex-1">
                        <Button className="w-full bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] hover:opacity-90 text-white">
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          立即购买
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-[#a0aec0]/30 mx-auto mb-4" />
              <p className="text-[#a0aec0]">没有找到匹配的产品</p>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0e27]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              购买流程
            </h2>
            <p className="text-[#a0aec0]">简单3步，即刻获取源码</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: 1,
                title: '选择产品',
                description: '浏览产品库，选择适合您需求的网站、软件或小程序源码',
                icon: Eye,
              },
              {
                step: 2,
                title: '在线支付',
                description: '支持微信、支付宝、银行卡等多种支付方式',
                icon: ShoppingCart,
              },
              {
                step: 3,
                title: '获取源码',
                description: '付款成功后立即收到下载链接和部署教程',
                icon: Download,
              },
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                {/* Step Number */}
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                {/* Connector Line */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)] h-px bg-gradient-to-r from-[#00d4ff]/50 to-[#7c3aed]/50" />
                )}
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-[#a0aec0]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#151b2e] to-[#0a0e27]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative p-8 rounded-2xl bg-gradient-to-r from-[#00d4ff]/10 to-[#7c3aed]/10 border border-[#00d4ff]/30">
            <div className="absolute inset-0 bg-[#151b2e]/50 rounded-2xl" />
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-white mb-4">
                没找到合适的产品？
              </h2>
              <p className="text-[#a0aec0] mb-8 max-w-xl mx-auto">
                我们提供定制开发服务，根据您的需求量身打造专属的网站、软件或小程序
              </p>
              <Link href="/services">
                <Button size="lg" className="bg-gradient-to-r from-[#00d4ff] to-[#7c3aed] hover:opacity-90 text-white px-8">
                  查看定制服务
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}